/**
 * Lightweight AST-to-string printer for GraphQL documents.
 *
 * Handles the 19 node kinds used by SDK codegen output:
 * Document, OperationDefinition, Field, Argument, Variable,
 * SelectionSet, NamedType, NonNullType, ListType, ObjectValue,
 * ObjectField, StringValue, IntValue, EnumValue, BooleanValue,
 * NullValue, ListValue, VariableDefinition, Name
 *
 * This is the fallback for documents without a pre-printed `__text` string
 * (e.g., documents created with `parse()` in test code).
 */

interface ASTNode {
  readonly kind: string
  readonly [key: string]: unknown
}

type NodePrinter = (node: ASTNode) => string

const printers: Record<string, NodePrinter> = {
  Document(node) {
    return printList(node.definitions as ASTNode[], '\n\n')
  },

  OperationDefinition(node) {
    const op = node.operation as string
    const name = node.name ? printNode(node.name as ASTNode) : ''
    const varDefs = node.variableDefinitions as ASTNode[] | undefined
    const vars = varDefs?.length ? `(${printList(varDefs, ', ')})` : ''
    const sel = printNode(node.selectionSet as ASTNode)
    return `${op}${name ? ` ${name}` : ''}${vars} ${sel}`
  },

  VariableDefinition(node) {
    const variable = printNode(node.variable as ASTNode)
    const type = printNode(node.type as ASTNode)
    const defaultValue = node.defaultValue
      ? ` = ${printNode(node.defaultValue as ASTNode)}`
      : ''
    return `${variable}: ${type}${defaultValue}`
  },

  SelectionSet(node) {
    const selections = node.selections as ASTNode[]
    return `{\n${indent(printList(selections, '\n'))}\n}`
  },

  Field(node) {
    const alias = node.alias ? `${printNode(node.alias as ASTNode)}: ` : ''
    const name = printNode(node.name as ASTNode)
    const args = node.arguments as ASTNode[] | undefined
    const argStr = args?.length ? `(${printList(args, ', ')})` : ''
    const sel = node.selectionSet
      ? ` ${printNode(node.selectionSet as ASTNode)}`
      : ''
    return `${alias}${name}${argStr}${sel}`
  },

  Argument(node) {
    const name = printNode(node.name as ASTNode)
    const value = printNode(node.value as ASTNode)
    return `${name}: ${value}`
  },

  Variable(node) {
    return `$${printNode(node.name as ASTNode)}`
  },

  Name(node) {
    return node.value as string
  },

  NamedType(node) {
    return printNode(node.name as ASTNode)
  },

  NonNullType(node) {
    return `${printNode(node.type as ASTNode)}!`
  },

  ListType(node) {
    return `[${printNode(node.type as ASTNode)}]`
  },

  ObjectValue(node) {
    const fields = node.fields as ASTNode[]
    return `{${printList(fields, ', ')}}`
  },

  ObjectField(node) {
    const name = printNode(node.name as ASTNode)
    const value = printNode(node.value as ASTNode)
    return `${name}: ${value}`
  },

  StringValue(node) {
    const value = node.value as string
    if (node.block) {
      return `"""\n${value}\n"""`
    }
    return JSON.stringify(value)
  },

  IntValue(node) {
    return node.value as string
  },

  FloatValue(node) {
    return node.value as string
  },

  EnumValue(node) {
    return node.value as string
  },

  BooleanValue(node) {
    return node.value ? 'true' : 'false'
  },

  NullValue() {
    return 'null'
  },

  ListValue(node) {
    const values = node.values as ASTNode[]
    return `[${printList(values, ', ')}]`
  },
}

function printNode(node: ASTNode): string {
  const printer = printers[node.kind]
  if (!printer) {
    throw new Error(`printDocument: unsupported AST node kind "${node.kind}"`)
  }
  return printer(node)
}

function printList(nodes: ASTNode[], separator: string): string {
  return nodes.map(printNode).join(separator)
}

function indent(str: string): string {
  return str
    .split('\n')
    .map((line) => (line ? `  ${line}` : line))
    .join('\n')
}

/**
 * Print a GraphQL document AST to a query string.
 *
 * This is a lightweight alternative to `print()` from the `graphql` package.
 * It supports the node kinds used by SDK codegen output. For documents with a
 * pre-printed `__text` property (set by codegen), use that instead — this
 * function is the fallback for documents without `__text`.
 *
 * Accepts any object with a `kind` property (e.g., `DocumentNode`,
 * `GqlDocumentNode`, or plain JSON AST objects from codegen output).
 */
export function printDocument(document: { readonly kind: string }): string {
  return printNode(document as ASTNode)
}
